<template>
  <div
    v-if="state"
    class="san-refinement-selector"
    :class="[options.length === 0 && 'no-refinement']"
  >
    <slot
      :options="options"
      :refine="refine"
      :search-for-items="state.searchForItems"
      :search-for-items-query="searchForFacetValuesQuery"
      :toggle-show-more="toggleShowMore"
      :can-toggle-show-more="state.canToggleShowMore"
      :is-showing-more="state.isShowingMore"
      :create-url="state.createUrl"
      :is-from-search="state.isFromSearch"
      :can-refine="state.canRefine"
      :send-event="state.sendEvent"
    >
      <slot
        v-if="state.isFromSearch && options.length === 0"
        name="noResults"
        :query="searchForFacetValues"
      >
        <div class="san-refinement-selector--no-results">
          No results.
        </div>
      </slot>

      <!-- @filter="onFilter"
        @add="onAdd" -->
      <!-- v-model="selected" -->
      <Multiselect
        ref="multiselectRef"
        :model-value="selected"
        class="san-refinement-selector--select"
        size="xs"
        :options="options"
        :option-label="optionLabel"
        :option-value="optionValue"
        :placeholder="options.length > 0 ? placeholder : disabledPlaceholder"
        :disable="options.length < 1"

        @add="refine"
        @remove="refine"
      >
        <!-- @update:model-value="val => refine(val)" -->
        <template #option="{ itemProps, opt }">
          <slot
            name="item"
            :item="opt"
            :refine="refine"
            :create-url="state.createUrl"
          >
            <q-item v-bind="itemProps">
              <!-- <q-item-section avatar>
                <q-icon
                  class="text-dark"
                  size="lg"
                >
                  <img src="/icons/flags/hr.svg">
                </q-icon>
              </q-item-section> -->
              <q-item-section>
                <q-item-label v-if="searchable">
                  <!-- <ais-highlight
                    attribute="item"
                    :hit="opt"
                  /> -->
                  {{ opt.label }}
                </q-item-label>
                <q-item-label v-else>
                  {{ opt.label }}
                </q-item-label>
              </q-item-section>
              <q-item-section side>
                <q-badge
                  color="neutral"
                  :label="opt.count"
                />
              </q-item-section>
            </q-item>
          </slot>
        </template>

        <template #selected-item="{ opt, removeAtIndex, index }">
          <q-chip
            icon-remove="mono-cross-circle-solid"
            dense
            removable
            @remove="removeAtIndex(index)"
          >
            {{ `${opt.label} (${opt.count})` }}
          </q-chip>
        </template>
      </Multiselect>

      <!-- <button
        v-if="showMore"
        class="san-refinement-selector--show-more"
        :class="[!state.canToggleShowMore && 'san-refinement-selector--show-more--disabled']"
        :disabled="!state.canToggleShowMore"
        @click="toggleShowMore"
      >
        <slot
          name="showMoreLabel"
          :is-showing-more="state.isShowingMore"
        >
          Show {{ state.isShowingMore ? 'less' : 'more' }}
        </slot>
      </button> -->
    </slot>
  </div>
</template>

<script setup>
import { inject, ref, computed, watch } from 'vue'
import { useConnector } from '@/composables'
import { connectRefinementList } from 'instantsearch.js/es/connectors'
import { useTranslation } from 'i18next-vue'
// import SearchInput from 'vue-instantsearch/src/components/SearchInput.vue'

defineOptions({ name: 'RefinementSelector' })
const hasSelected = defineModel('hasSelected', { type: Boolean, default: false })
const props = defineProps({
  attribute: { type: String, required: true },
  searchable: { type: Boolean, default: undefined },
  searchablePlaceholder: { type: String, required: false, default: 'Search here…' },
  operator: { default: 'or', validator (value) {
    return value === 'and' || value === 'or'
  }, required: false },
  limit: { type: Number, required: false, default: 999999 },
  showMoreLimit: { type: Number, required: false, default: 999999 },
  showMore: { type: Boolean, required: false, default: false },
  sortBy: { type: [Array, Function], required: false, default: undefined },
  transformItems: { type: Function, required: false, default: undefined },
  placeholder: { type: String, default: null },
  disabledPlaceholder: { type: String, default: null },
  optionLabel: { type: String, default: 'label' },
  optionValue: { type: String, default: 'value' },
  labelTranslationString: { type: String, default: null }
})

const $emitter = inject('instantSearchPanelEmitter', { emit: () => {} })

const { t } = useTranslation()

const noop = () => {}

// const state = useConnector(connectRefinementList, {
//   attribute: props.attribute,
//   operator: props.operator,
//   limit: props.limit,
//   showMore: props.showMore,
//   showMoreLimit: props.showMoreLimit,
//   sortBy: props.sortBy,
//   escapeFacetValues: true,
//   transformItems: props.transformItems
// })
const state = useConnector(renderFn => connectRefinementList((renderOptions, isFirstRender) => {
  const { instantSearchInstance, sendEvent } = renderOptions // eslint-disable-line
  const { helper, mainHelper: { derivedHelpers } } = instantSearchInstance // eslint-disable-line

  // console.log('%cconnectRefinementList', 'color:magenta', helper)

  renderFn({
    ...renderOptions,
    refine: facetValue => {
      sendEvent('click:internal', facetValue)
      // helper.setQueryParameter('facet_return_parent', props.attribute)
      // helper.setQueryParameter('hitsPerPage', 50)
      // helper.setQuery('*')
      helper.toggleFacetRefinement(props.attribute, facetValue)
      // console.log({ attribute: props.attribute, facetValue, query: helper.getQuery(), helper })
      helper.search()
    }
  }, isFirstRender)
}, params => {
  // console.log('%cunmount', 'color:orange')
}), {
  $$widgetType: 'ais.refinementList',
  attribute: props.attribute,
  operator: props.operator,
  limit: props.limit,
  showMore: props.showMore,
  showMoreLimit: props.showMoreLimit,
  sortBy: props.sortBy,
  escapeFacetValues: true,
  transformItems: props.transformItems
})

const multiselectRef = ref()
const searchForFacetValuesQuery = ref('')
const hasEmitted = ref(false)

const selected = computed(() => state.value?.items?.filter(i => i.isRefined))

const searchForFacetValues = computed({
  get: () => {
    return searchForFacetValuesQuery.value
  },
  set: value => {
    state.value.searchForItems(value)
    searchForFacetValuesQuery.value = value
  }
})

const toggleShowMore = computed(() => {
  return state.value.toggleShowMore || noop
})

const options = computed(() => {
  const data = state.value.items.map(item => {
    const label = props.labelTranslationString
      ? t(`${props.labelTranslationString}.${item.label}`)
      : item.label

    return Object.assign({}, item, {
      _highlightResult: {
        item: {
          value: item.highlighted
        }
      },
      label
    })
  })

  return data
})

const refine = ({ index, option }) => {
  state.value.refine(option.value)
  searchForFacetValuesQuery.value = ''
}

const mapStateToCanRefine = s => !!s.canRefine

const clearSelected = () => {
  // multiselectRef.value.clear()
  // console.log(state.value)
}

watch(() => state.value, (newState, oldState) => {
  if (!newState) return

  const previousCanRefine = mapStateToCanRefine(oldState || {})
  const nextCanRefine = mapStateToCanRefine(newState)

  // console.log('%cstate', 'color:orange', newState.items.filter(i => i.isRefined).map(i => i.value))

  if (!hasEmitted.value || previousCanRefine !== nextCanRefine) {
    $emitter.emit('PANEL_CHANGE_EVENT', nextCanRefine)
    hasEmitted.value = true
  }
}, { immediate: true })

watch(() => selected.value, val => {
  hasSelected.value = val.length > 0
})

defineExpose({ clearSelected })
</script>
